import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { DietaryTag, PricingType } from "@/types/menu";
import { useAuth } from "@/contexts/AuthContext";
import type { TablesUpdate } from "@/integrations/supabase/types";

export interface MenuItemRow {
  id: string;
  category_id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  is_active: boolean;
  dietary_tags: string[];
  sku: string | null;
  abbreviation: string | null;
  square_catalog_id: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  category?: { id: string; name: string } | null;
  prices?: PriceRow[];
  created_by_profile?: { display_name: string | null } | null;
  updated_by_profile?: { display_name: string | null } | null;
}

export interface PriceRow {
  id: string;
  menu_item_id: string;
  pricing_type: string;
  price: number;
  label: string | null;
  serves: number | null;
  sku: string | null;
  created_at: string;
}

export function useMenuItems() {
  return useQuery({
    queryKey: ["menu_items"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("menu_items")
        .select("*, category:menu_categories(id, name), prices:menu_item_prices(*), created_by_profile:profiles!menu_items_created_by_fkey(display_name), updated_by_profile:profiles!menu_items_updated_by_fkey(display_name)")
        .order("name");
      if (error) throw error;
      return data as MenuItemRow[];
    },
  });
}

export interface CreateMenuItemInput {
  name: string;
  description?: string;
  category_id: string;
  image_url?: string | null;
  is_active?: boolean;
  dietary_tags?: string[];
  sku?: string;
  abbreviation?: string;
  prices?: { pricing_type: string; price: number; label?: string; serves?: number; sku?: string }[];
}

export function useCreateMenuItem() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ prices, ...values }: CreateMenuItemInput) => {
      const { data, error } = await supabase
        .from("menu_items")
        .insert({ ...values, created_by: user?.id, updated_by: user?.id })
        .select()
        .single();
      if (error) throw error;
      if (prices && prices.length > 0) {
        const { error: pe } = await supabase
          .from("menu_item_prices")
          .insert(prices.map((p) => ({ ...p, menu_item_id: data.id })));
        if (pe) throw pe;
      }
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["menu_items"] });
      toast.success("Menu item created");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdateMenuItem() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, prices, ...values }: { id: string; prices?: { pricing_type: string; price: number; label?: string; serves?: number; sku?: string }[] } & Partial<CreateMenuItemInput>) => {
      const { error } = await supabase
        .from("menu_items")
        .update({ ...values, updated_by: user?.id })
        .eq("id", id);
      if (error) throw error;
      if (prices) {
        const { error: de } = await supabase.from("menu_item_prices").delete().eq("menu_item_id", id);
        if (de) throw de;
        if (prices.length > 0) {
          const { error: pe } = await supabase
            .from("menu_item_prices")
            .insert(prices.map((p) => ({ ...p, menu_item_id: id })));
          if (pe) throw pe;
        }
      }
    },
    onSuccess: () => {
      toast.success("Menu item updated");
    },
    // Refresh on failure too: the menu item row may be saved even when its prices weren't (#645).
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["menu_items"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDeleteMenuItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("menu_items").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["menu_items"] });
      toast.success("Menu item deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useBulkUpdateMenuItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, updates }: { ids: string[]; updates: TablesUpdate<"menu_items"> }) => {
      const { error } = await supabase
        .from("menu_items")
        .update(updates)
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["menu_items"] });
      toast.success("Items updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useBulkDeleteMenuItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase
        .from("menu_items")
        .delete()
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["menu_items"] });
      toast.success("Items deleted");
    },
    onError: (e: Error) => {
      if (e.message?.includes("foreign key") || e.message?.includes("violates")) {
        toast.error("Some items couldn't be deleted because they're referenced in orders or quotes. Try archiving them instead.");
      } else {
        toast.error(e.message);
      }
    },
  });
}

export function useBulkUpdateDietaryTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ items, addTags, removeTags }: { items: MenuItemRow[]; addTags?: string[]; removeTags?: string[] }) => {
      const updates = items.map((item) => {
        let tags = [...(item.dietary_tags ?? [])];
        if (addTags?.length) {
          tags = [...new Set([...tags, ...addTags])];
        }
        if (removeTags?.length) {
          tags = tags.filter((t) => !removeTags.includes(t));
        }
        return supabase
          .from("menu_items")
          .update({ dietary_tags: tags })
          .eq("id", item.id);
      });
      const results = await Promise.all(updates);
      const failed = results.find((r) => r.error);
      if (failed?.error) throw failed.error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["menu_items"] });
      toast.success("Dietary tags updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
